import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useCompanyDetail } from "../hooks/useCompanies";
import {
  usePaymentMethods,
  useUpsertDispatchSettings,
} from "../hooks/useCompanySettings";

interface CompanyAdditionalInfoPanelProps {
  companyGuid: string;
}

/**
 * 주선사에 기본 결제타입이 설정되지 않았을 때 쓰는 시스템 기본값.
 * 서버(DispatchCompanyDetailDto.FallbackPayMethod) · ULA(SYSTEM_DEFAULT_PAY_METHOD)와 반드시 같아야 한다.
 */
const FALLBACK_PAY_METHOD = "신용";

/**
 * 화물 비고1/2 의 기본 표시 라벨.
 * 서버(DispatchCompanyDetailDto.DefaultRemark{1,2}Label) · ULA(DEFAULT_REMARK_LABELS)와 반드시 같아야 한다.
 */
const DEFAULT_REMARK1_LABEL = "비고1";
const DEFAULT_REMARK2_LABEL = "비고2";

/** 비고 라벨 길이 제한 — 서버 컬럼(varchar 32)과 같아야 한다 */
const REMARK_LABEL_MAX_LENGTH = 32;

/**
 * 비고 노출 개수 선택지. 상한 2 는 값 컬럼(ul_cargo_extension.remark1/2) 개수와 같다 —
 * 서버(DispatchCompanyDetailDto.MaxRemarkCount) · ULA(MAX_REMARK_COUNT)와 반드시 같아야 한다.
 * 슬롯은 앞에서부터 채운다(1개 = 비고1만) — 중간을 건너뛸 수 없다.
 */
const REMARK_COUNT_OPTIONS = [
  { value: "0", label: "사용 안 함 (기본)" },
  { value: "1", label: "1개 — 비고1만" },
  { value: "2", label: "2개 — 비고1 · 비고2" },
] as const;

// 화물리스트 기본 정렬 프리셋 — ULA 프론트 cargoSortPresets와 값("<컬럼>_<방향>") 동기화 필요
const CARGO_SORT_OPTIONS = [
  { value: "LOAD_ASC", label: "상차시간 오름차순 (기본)" },
  { value: "LOAD_DESC", label: "상차시간 내림차순" },
  { value: "DROP_ASC", label: "하차시간 오름차순" },
  { value: "DROP_DESC", label: "하차시간 내림차순" },
  { value: "DISPATCH_DESC", label: "최근 배차순 (배차시간 내림차순)" },
  { value: "DISPATCH_ASC", label: "배차시간 오름차순" },
  { value: "REGIST_DESC", label: "최근 등록순 (등록시간 내림차순)" },
  { value: "REGIST_ASC", label: "등록시간 오름차순" },
] as const;

interface FormValues {
  smsBrandName: string;
  smsSenderNumber: string;
  postalZipCode: string;
  postalAddress: string;
  postalRecipient: string;
  isAutoCompleteStateEnabled: boolean;
  isCompletedEditGuardEnabled: boolean;
  useCommonPriceFallback: boolean;
  defaultCargoSort: string;
  isSnsCargoEnabled: boolean;
  isDriverListEnabled: boolean;
  defaultPayMethod: string;
  cargoRemark1Label: string;
  cargoRemark2Label: string;
  /** Select 가 문자열만 다루므로 폼에서는 "0"|"1"|"2" 로 들고, 저장 시 숫자로 되돌린다 */
  cargoRemarkCount: string;
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-700">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

/**
 * 항목 성격별 묶음. 이 탭은 값 종류가 계속 늘어나는 곳이라 평평하게 나열하면
 * 무엇을 바꾸는 설정인지 구분이 안 된다.
 * `separated` 는 항목마다 구분선이 필요한 묶음(토글 목록 등)에 쓴다.
 */
function Section({
  title,
  description,
  separated,
  children,
}: {
  title: string;
  description?: string;
  separated?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white">
      <header className="border-b border-gray-100 px-3 py-2">
        <h5 className="text-xs font-semibold text-gray-900">{title}</h5>
        {description && (
          <p className="mt-0.5 text-[11px] leading-tight text-gray-500">
            {description}
          </p>
        )}
      </header>
      <div
        className={cn(
          "flex flex-col p-3",
          separated
            ? "[&>*+*]:mt-3 [&>*+*]:border-t [&>*+*]:border-gray-100 [&>*+*]:pt-3"
            : "gap-3",
        )}
      >
        {children}
      </div>
    </section>
  );
}

/** 스위치 4개가 같은 모양이라 한 곳으로 모은다 — 경고 문구 유무만 다르다. */
function ToggleRow({
  label,
  description,
  warning,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  warning?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <label className="text-xs font-medium text-gray-700">{label}</label>
        <p className="mt-1 text-xs text-gray-400">{description}</p>
        {warning && (
          <p className="mt-1 text-xs font-medium text-amber-600">{warning}</p>
        )}
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        size="sm"
        className="mt-0.5 shrink-0"
      />
    </div>
  );
}

export function CompanyAdditionalInfoPanel({
  companyGuid,
}: CompanyAdditionalInfoPanelProps) {
  const { data: detail, isLoading } = useCompanyDetail(companyGuid);
  const { data: payMethods = [] } = usePaymentMethods();
  const upsert = useUpsertDispatchSettings(companyGuid);

  const { register, handleSubmit, watch, setValue } = useForm<FormValues>({
    values: {
      smsBrandName: detail?.smsBrandName ?? "",
      smsSenderNumber: detail?.smsSenderNumber ?? "",
      postalZipCode: detail?.postalZipCode ?? "",
      postalAddress: detail?.postalAddress ?? "",
      postalRecipient: detail?.postalRecipient ?? "",
      isAutoCompleteStateEnabled: detail?.isAutoCompleteStateEnabled ?? false,
      isCompletedEditGuardEnabled: detail?.isCompletedEditGuardEnabled ?? false,
      // 위 둘과 달리 기본값이 true다 — 이 값이 false로 새면 견적이 통째로 0원(협의)이 된다.
      useCommonPriceFallback: detail?.useCommonPriceFallback ?? true,
      defaultCargoSort: detail?.defaultCargoSort ?? "LOAD_ASC",
      isSnsCargoEnabled: detail?.isSnsCargoEnabled ?? false,
      isDriverListEnabled: detail?.isDriverListEnabled ?? false,
      defaultPayMethod: detail?.defaultPayMethod ?? FALLBACK_PAY_METHOD,
      cargoRemark1Label: detail?.cargoRemark1Label ?? DEFAULT_REMARK1_LABEL,
      cargoRemark2Label: detail?.cargoRemark2Label ?? DEFAULT_REMARK2_LABEL,
      cargoRemarkCount: String(detail?.cargoRemarkCount ?? 0),
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="text-xs text-gray-400">로딩 중...</span>
      </div>
    );
  }

  // 저장된 값이 sys item 목록에 없거나 목록 조회가 실패해도 선택지가 비지 않도록 합집합으로 구성
  const selectedPayMethod = watch("defaultPayMethod");
  const payMethodOptions = [
    ...new Set([...payMethods, FALLBACK_PAY_METHOD, selectedPayMethod]),
  ].filter(Boolean);

  // 열려 있는 비고 슬롯 개수 — 이름 입력칸 잠금에 쓴다
  const remarkCount = Number(watch("cargoRemarkCount"));
  // 잠긴 칸은 dim 만으로는 이유를 알 수 없다 — 몇 개로 올려야 열리는지 같이 적는다.
  const remarkLockHint = (slot: number) =>
    remarkCount < slot ? `사용 개수를 ${slot}개 이상으로 올리면 입력할 수 있습니다` : undefined;

  const onSubmit = (v: FormValues) => {
    upsert.mutate({
      CompanyGuid: companyGuid,
      SmsBrandName: v.smsBrandName.trim() || null,
      SmsSenderNumber: v.smsSenderNumber.trim() || null,
      PostalZipCode: v.postalZipCode.trim() || null,
      PostalAddress: v.postalAddress.trim() || null,
      PostalRecipient: v.postalRecipient.trim() || null,
      IsAutoCompleteStateEnabled: v.isAutoCompleteStateEnabled,
      IsCompletedEditGuardEnabled: v.isCompletedEditGuardEnabled,
      UseCommonPriceFallback: v.useCommonPriceFallback,
      DefaultCargoSort: v.defaultCargoSort,
      IsSnsCargoEnabled: v.isSnsCargoEnabled,
      IsDriverListEnabled: v.isDriverListEnabled,
      DefaultPayMethod: v.defaultPayMethod,
      // 빈 값이면 빈 문자열로 보내 서버가 기본 라벨로 되돌리게 한다(null 은 "변경 안 함"이라 리셋이 안 된다).
      CargoRemark1Label: v.cargoRemark1Label.trim(),
      CargoRemark2Label: v.cargoRemark2Label.trim(),
      CargoRemarkCount: Number(v.cargoRemarkCount),
    });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-1 flex-col overflow-hidden"
    >
      {/* 묶음이 늘어나 스크롤이 길어졌다 — 저장 버튼은 스크롤 밖에 고정한다 */}
      <div className="flex flex-1 flex-col gap-3 overflow-y-auto bg-gray-50/60 p-4">
        <Section
          title="발송 정보"
          description="SMS·메일 템플릿에 치환되는 발신 정보와, 인수증·세금계산서 우편 송부처"
        >
          <Field
            label="SMS 브랜드명"
            hint="SMS 제목의 [브랜드명]. 미설정 시 회사명으로 대체됩니다."
          >
            <Input
              {...register("smsBrandName")}
              placeholder="예: 나래화물"
              className="h-8 text-xs"
            />
          </Field>

          <Field
            label="게스트 SMS 발신번호"
            hint="게스트 화물등록 안내 SMS의 발신번호. 미설정 시 기본 발신번호로 발송됩니다."
          >
            <Input
              {...register("smsSenderNumber")}
              placeholder="예: 031-222-3344"
              inputMode="numeric"
              className="h-8 text-xs"
            />
          </Field>

          <div className="grid grid-cols-3 gap-2">
            <Field label="우편번호">
              <Input
                {...register("postalZipCode")}
                placeholder="예: 08594"
                className="h-8 text-xs"
              />
            </Field>
            <div className="col-span-2">
              <Field label="우편 송부주소">
                <Input
                  {...register("postalAddress")}
                  placeholder="예: 서울특별시 금천구 ..."
                  className="h-8 text-xs"
                />
              </Field>
            </div>
          </div>

          <Field label="수취 부서/담당" hint="인수증·세금계산서 우편물의 수취인">
            <Input
              {...register("postalRecipient")}
              placeholder="예: 정산팀"
              className="h-8 text-xs"
            />
          </Field>
        </Section>

        <Section
          title="기능 사용 설정"
          description="이 업체에서 켤 기능을 고릅니다. 끄면 관련 화면·자동 동작이 사라집니다."
          separated
        >
          <ToggleRow
            label="자동 완료 배치 사용"
            description="매일 새벽 2시, 배차 상태이고 상차일시가 어제 이전인 화물을 완료로 자동 전환"
            checked={watch("isAutoCompleteStateEnabled")}
            onChange={(v) =>
              setValue("isAutoCompleteStateEnabled", v, { shouldDirty: true })
            }
          />

          <ToggleRow
            label="완료 화물 수정 보호"
            description="켜면 완료 상태 화물의 수정·삭제·운임 변경이 order-super-write 권한 보유자에게만 허용됩니다"
            checked={watch("isCompletedEditGuardEnabled")}
            onChange={(v) =>
              setValue("isCompletedEditGuardEnabled", v, { shouldDirty: true })
            }
          />

          <ToggleRow
            label="공통 단가 폴백 사용"
            description="단가는 계약 → 표준 → 공통(기본 셋팅) 순으로 조회합니다. 끄면 마지막 공통 단계를 건너뛰어, 계약·표준에 없는 구간은 0원 + '협의'로 응답합니다. 지역·거리·경유 조회 전부에 적용됩니다."
            warning={
              !watch("useCommonPriceFallback")
                ? "꺼짐 — 계약·표준 단가가 없는 구간은 견적이 0원으로 나갑니다."
                : undefined
            }
            checked={watch("useCommonPriceFallback")}
            onChange={(v) =>
              setValue("useCommonPriceFallback", v, { shouldDirty: true })
            }
          />

          <ToggleRow
            label="SNS 화물 관리 사용"
            description="카카오톡 등 SNS 채팅 원문에서 화물을 접수하는 화면을 엽니다. 채팅 수집기(CaptainHook)를 실제로 돌리는 업체만 켜세요 — 끄면 메뉴·화면과 채팅 조회 API가 모두 차단됩니다."
            warning={
              watch("isSnsCargoEnabled")
                ? "켜짐 — 수집기가 없으면 화면에 채팅방이 하나도 뜨지 않습니다."
                : undefined
            }
            checked={watch("isSnsCargoEnabled")}
            onChange={(v) =>
              setValue("isSnsCargoEnabled", v, { shouldDirty: true })
            }
          />

          <ToggleRow
            label="차주 리스트 사용"
            description="화물 리스트에 [차주 리스트] 탭을 엽니다. 화물을 차주 × 운행일로 묶어 그날 몇 회전을 뛰었는지 봅니다 — 고정·지입 차주를 운영하는 업체용."
            checked={watch("isDriverListEnabled")}
            onChange={(v) =>
              setValue("isDriverListEnabled", v, { shouldDirty: true })
            }
          />
        </Section>

        <Section
          title="배차 기본값"
          description="이 업체 소속 사용자가 화물을 등록·조회할 때 처음 적용되는 값"
          separated
        >
          <Field
            label="신규 화물 기본 결제타입"
            hint={`화주에 기본 결제타입이 설정된 경우엔 화주 값이 우선합니다. 미설정 화주로 등록할 때 이 값이 들어갑니다. (기본: ${FALLBACK_PAY_METHOD})`}
          >
            <Select
              value={watch("defaultPayMethod")}
              onValueChange={(v) =>
                setValue("defaultPayMethod", v ?? FALLBACK_PAY_METHOD, {
                  shouldDirty: true,
                })
              }
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {payMethodOptions.map((m) => (
                  <SelectItem key={m} value={m} className="text-xs">
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field
            label="화물리스트 기본 정렬"
            hint="이 업체 소속 사용자의 화물리스트 초기 정렬 기준. 개인이 컬럼 헤더로 바꾸면 그 세션에선 덮어씁니다."
          >
            <Select
              value={watch("defaultCargoSort")}
              onValueChange={(v) =>
                setValue("defaultCargoSort", v ?? "LOAD_ASC", {
                  shouldDirty: true,
                })
              }
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CARGO_SORT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} className="text-xs">
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div>
            <p className="mb-1 text-xs font-medium text-gray-700">화물 비고 항목</p>
            <p className="mb-2 text-xs text-gray-400">
              업체가 자유롭게 쓰는 화물 항목입니다(차수·발주처·관리번호 등). 몇 개를 열지 여기서 정하면
              화물 목록 컬럼과 화물 상세 &quot;기타 정보&quot; 탭이 그만큼만 나타납니다. 값 자체는 화물마다
              입력하고, 항목 이름은 비우면 기본 이름으로 되돌아갑니다.
            </p>
            <div className="grid grid-cols-3 gap-2">
              <Field label="사용 개수">
                <Select
                  value={watch("cargoRemarkCount")}
                  onValueChange={(v) =>
                    setValue("cargoRemarkCount", v ?? "0", { shouldDirty: true })
                  }
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REMARK_COUNT_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} className="text-xs">
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {/* 열지 않은 슬롯의 이름은 잠그되 값은 지우지 않는다 — 개수를 되돌리면 쓰던 이름이 그대로 살아난다 */}
              <Field label="비고1 항목명" hint={remarkLockHint(1)}>
                <Input
                  {...register("cargoRemark1Label")}
                  disabled={remarkCount < 1}
                  maxLength={REMARK_LABEL_MAX_LENGTH}
                  placeholder={DEFAULT_REMARK1_LABEL}
                  className="h-8 text-xs"
                />
              </Field>
              <Field label="비고2 항목명" hint={remarkLockHint(2)}>
                <Input
                  {...register("cargoRemark2Label")}
                  disabled={remarkCount < 2}
                  maxLength={REMARK_LABEL_MAX_LENGTH}
                  placeholder={DEFAULT_REMARK2_LABEL}
                  className="h-8 text-xs"
                />
              </Field>
            </div>
            {remarkCount === 0 && (
              <p className="mt-1 text-xs text-gray-400">
                사용 안 함이면 이름을 정해 두어도 ULA 화면에는 비고가 보이지 않습니다.
              </p>
            )}
          </div>
        </Section>
      </div>

      <div className="flex justify-end border-t border-gray-200 bg-white px-4 py-2">
        <Button
          type="submit"
          size="sm"
          disabled={upsert.isPending}
          className="h-7 bg-blue-600 px-3 text-xs font-bold hover:bg-blue-700"
        >
          {upsert.isPending ? "저장 중..." : "저장"}
        </Button>
      </div>
    </form>
  );
}
